import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Mail, 
  Server, 
  Zap, 
  AlertTriangle, 
  CheckCircle, 
  Save, 
  RefreshCw,
  Send,
  Key,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EmailProviderSettings = () => {
  const [settings, setSettings] = useState({
    active_provider: 'none',
    resend_api_key: '',
    resend_api_key_masked: '',
    resend_from_email: 'onboarding@resend.dev',
    resend_from_name: 'JEE Predictor',
    smtp_enabled: true,
    resend_enabled: true
  });
  const [status, setStatus] = useState({
    smtp_configured: false,
    resend_configured: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [testingResend, setTestingResend] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [newApiKey, setNewApiKey] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/email-provider/settings`);
      if (response.data.success) {
        setSettings(response.data.settings);
        setStatus(response.data.status);
      }
    } catch (error) {
      console.error('Error fetching email provider settings:', error);
      toast({
        title: "Error",
        description: "Failed to load email provider settings",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const params = new URLSearchParams({
        active_provider: settings.active_provider,
        resend_from_email: settings.resend_from_email,
        resend_from_name: settings.resend_from_name,
        smtp_enabled: settings.smtp_enabled.toString(),
        resend_enabled: settings.resend_enabled.toString()
      });
      
      // Only include API key if a new one was entered
      if (newApiKey) {
        params.append('resend_api_key', newApiKey);
      }
      
      const response = await axios.put(
        `${BACKEND_URL}/api/admin/email-provider/settings?${params.toString()}`
      );
      
      if (response.data.success) {
        toast({
          title: "Success",
          description: "Email provider settings updated successfully"
        });
        setNewApiKey('');
        fetchSettings(); // Refresh to get masked key
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: "Error",
        description: "Failed to save email provider settings",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestSmtp = async () => {
    if (!testEmail) {
      toast({
        title: "Error",
        description: "Please enter a test email address",
        variant: "destructive"
      });
      return;
    }
    
    setTestingSmtp(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/smtp/test?test_email=${encodeURIComponent(testEmail)}`
      );
      toast({
        title: "Success",
        description: "Test email sent via SMTP successfully"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to send test email via SMTP",
        variant: "destructive"
      });
    } finally {
      setTestingSmtp(false);
    }
  };

  const handleTestResend = async () => {
    if (!testEmail) {
      toast({
        title: "Error",
        description: "Please enter a test email address",
        variant: "destructive"
      });
      return;
    }
    
    setTestingResend(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/email-provider/test-resend?test_email=${encodeURIComponent(testEmail)}`
      );
      toast({
        title: "Success",
        description: "Test email sent via Resend successfully"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to send test email via Resend",
        variant: "destructive"
      });
    } finally {
      setTestingResend(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className={status.smtp_configured ? "border-green-200 bg-green-50" : "border-yellow-200 bg-yellow-50"}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${status.smtp_configured ? 'bg-green-100' : 'bg-yellow-100'}`}>
                <Server className={`w-5 h-5 ${status.smtp_configured ? 'text-green-600' : 'text-yellow-600'}`} />
              </div>
              <div>
                <p className="font-medium text-slate-900">SMTP</p>
                <div className="flex items-center gap-2">
                  {status.smtp_configured ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-700">Configured</span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="w-4 h-4 text-yellow-600" />
                      <span className="text-sm text-yellow-700">Not Configured</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={status.resend_configured ? "border-green-200 bg-green-50" : "border-yellow-200 bg-yellow-50"}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${status.resend_configured ? 'bg-green-100' : 'bg-yellow-100'}`}>
                <Zap className={`w-5 h-5 ${status.resend_configured ? 'text-green-600' : 'text-yellow-600'}`} />
              </div>
              <div>
                <p className="font-medium text-slate-900">Resend</p>
                <div className="flex items-center gap-2">
                  {status.resend_configured ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-700">Configured</span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="w-4 h-4 text-yellow-600" />
                      <span className="text-sm text-yellow-700">Not Configured</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Provider Selection */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600" />
            <CardTitle>Email Provider Selection</CardTitle>
          </div>
          <CardDescription>
            Choose which email provider to use for password reset and notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* No Provider */}
          <div 
            className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-all ${
              settings.active_provider === 'none' 
                ? 'bg-slate-100 border-slate-400' 
                : 'bg-slate-50 border-slate-200 hover:border-slate-300'
            }`}
            onClick={() => setSettings({ ...settings, active_provider: 'none' })}
          >
            <div className="flex items-center gap-3">
              <input 
                type="radio" 
                checked={settings.active_provider === 'none'} 
                onChange={() => setSettings({ ...settings, active_provider: 'none' })}
                className="w-4 h-4"
              />
              <div>
                <p className="font-medium text-slate-900">Disabled</p>
                <p className="text-sm text-slate-500">No emails will be sent (reset links logged to console)</p>
              </div>
            </div>
            <Badge variant="secondary">Off</Badge>
          </div>

          {/* Resend Provider */}
          <div 
            className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-all ${
              settings.active_provider === 'resend' 
                ? 'bg-purple-50 border-purple-400' 
                : 'bg-slate-50 border-slate-200 hover:border-slate-300'
            }`}
            onClick={() => settings.resend_enabled && setSettings({ ...settings, active_provider: 'resend' })}
          >
            <div className="flex items-center gap-3">
              <input 
                type="radio" 
                checked={settings.active_provider === 'resend'} 
                onChange={() => setSettings({ ...settings, active_provider: 'resend' })}
                disabled={!settings.resend_enabled}
                className="w-4 h-4"
              />
              <div>
                <p className="font-medium text-slate-900 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-purple-600" />
                  Resend (Recommended)
                </p>
                <p className="text-sm text-slate-500">Modern email API with 3000 free emails/month</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {status.resend_configured ? (
                <Badge className="bg-green-100 text-green-700">Ready</Badge>
              ) : (
                <Badge variant="outline" className="text-yellow-600 border-yellow-400">Setup Required</Badge>
              )}
            </div>
          </div>

          {/* SMTP Provider */}
          <div 
            className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-all ${
              settings.active_provider === 'smtp' 
                ? 'bg-blue-50 border-blue-400' 
                : 'bg-slate-50 border-slate-200 hover:border-slate-300'
            }`}
            onClick={() => settings.smtp_enabled && setSettings({ ...settings, active_provider: 'smtp' })}
          >
            <div className="flex items-center gap-3">
              <input 
                type="radio" 
                checked={settings.active_provider === 'smtp'} 
                onChange={() => setSettings({ ...settings, active_provider: 'smtp' })}
                disabled={!settings.smtp_enabled}
                className="w-4 h-4"
              />
              <div>
                <p className="font-medium text-slate-900 flex items-center gap-2">
                  <Server className="w-4 h-4 text-blue-600" />
                  SMTP
                </p>
                <p className="text-sm text-slate-500">Traditional email server (Gmail, SendGrid SMTP, etc.)</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {status.smtp_configured ? (
                <Badge className="bg-green-100 text-green-700">Ready</Badge>
              ) : (
                <Badge variant="outline" className="text-yellow-600 border-yellow-400">Setup Required</Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Resend Configuration */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-purple-600" />
            <CardTitle>Resend Configuration</CardTitle>
          </div>
          <CardDescription>
            Get your API key from <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">resend.com/api-keys</a>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* API Key */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Key className="w-4 h-4" />
              Resend API Key
            </Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  type={showApiKey ? "text" : "password"}
                  placeholder={settings.resend_api_key_masked || "re_xxxxx..."}
                  value={newApiKey}
                  onChange={(e) => setNewApiKey(e.target.value)}
                  className="pr-10"
                  data-testid="resend-api-key-input"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                >
                  {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {settings.resend_api_key_masked && !newApiKey && (
              <p className="text-xs text-green-600">Current key: {settings.resend_api_key_masked}</p>
            )}
            <p className="text-xs text-slate-500">Leave empty to keep existing key</p>
          </div>

          {/* From Email */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>From Name</Label>
              <Input
                value={settings.resend_from_name}
                onChange={(e) => setSettings({ ...settings, resend_from_name: e.target.value })}
                placeholder="JEE Predictor"
                data-testid="resend-from-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>From Email</Label>
              <Input
                value={settings.resend_from_email}
                onChange={(e) => setSettings({ ...settings, resend_from_email: e.target.value })}
                placeholder="onboarding@resend.dev"
                data-testid="resend-from-email-input"
              />
              <p className="text-xs text-slate-500">Use verified domain or onboarding@resend.dev for testing</p>
            </div>
          </div>

          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <Label className="text-sm font-medium">Enable Resend Option</Label>
              <p className="text-xs text-slate-500">Show Resend as an available provider</p>
            </div>
            <Switch
              checked={settings.resend_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, resend_enabled: checked })}
              data-testid="resend-enabled-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* SMTP Enable Toggle */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Server className="w-5 h-5 text-blue-600" />
            <CardTitle>SMTP Options</CardTitle>
          </div>
          <CardDescription>
            Configure SMTP in the Settings tab. Here you can enable/disable it as a provider option.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <Label className="text-sm font-medium">Enable SMTP Option</Label>
              <p className="text-xs text-slate-500">Show SMTP as an available provider</p>
            </div>
            <Switch
              checked={settings.smtp_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, smtp_enabled: checked })}
              data-testid="smtp-enabled-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* Test Email Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Send className="w-5 h-5 text-green-600" />
            <CardTitle>Test Email Configuration</CardTitle>
          </div>
          <CardDescription>
            Send a test email to verify your configuration
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="Enter test email address"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="flex-1"
              data-testid="test-email-input"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleTestSmtp}
              disabled={testingSmtp || !status.smtp_configured}
              className="flex-1"
              data-testid="test-smtp-btn"
            >
              {testingSmtp ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Server className="w-4 h-4 mr-2" />
              )}
              Test SMTP
            </Button>
            <Button
              variant="outline"
              onClick={handleTestResend}
              disabled={testingResend || !status.resend_configured}
              className="flex-1 text-purple-700 border-purple-300 hover:bg-purple-50"
              data-testid="test-resend-btn"
            >
              {testingResend ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Zap className="w-4 h-4 mr-2" />
              )}
              Test Resend
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-email-provider-settings">
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Email Settings
            </>
          )}
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <h4 className="font-medium text-blue-900 mb-2">Quick Setup Guide</h4>
          <div className="text-sm text-blue-800 space-y-2">
            <p><strong>Resend (Recommended):</strong></p>
            <ol className="list-decimal list-inside ml-2 space-y-1">
              <li>Sign up at <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="underline">resend.com</a> (free tier: 3000 emails/month)</li>
              <li>Go to API Keys and create a new key</li>
              <li>Paste the key above and click Save</li>
              <li>Select &quot;Resend&quot; as active provider</li>
            </ol>
            <p className="mt-3"><strong>SMTP:</strong></p>
            <ol className="list-decimal list-inside ml-2 space-y-1">
              <li>Go to Settings tab → SMTP Settings</li>
              <li>Enter your SMTP server details (Gmail, SendGrid, etc.)</li>
              <li>Come back here and select &quot;SMTP&quot; as active provider</li>
            </ol>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailProviderSettings;
